import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, MessageCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { CONTACT, PRODUCTS, SPORTS } from "@/data/content";

const SPORT_LINKS = [
  { to: "/hockey", label: "Hockey" },
  { to: "/team-sports#soccer", label: "Soccer" },
  { to: "/team-sports#basketball", label: "Basketball" },
  { to: "/team-sports#rugby", label: "Rugby" },
  { to: "/team-sports#volleyball", label: "Volleyball" },
  { to: "/team-sports#baseball", label: "Baseball" },
  { to: "/activewear", label: "Activewear" },
];

const COMPANY_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/manufacturing", label: "Manufacturing" },
  { to: "/contact", label: "Contact" },
  { to: "/contact", label: "Request a Quote" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3">
          <Logo light className="h-16" />
          <p className="mt-5 text-sm text-slate-300 leading-relaxed">
            Custom sportswear and teamwear, manufactured around your design — with ice
            hockey at the heart of the range.
          </p>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-whatsapp-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ice hover:text-white transition-colors"
          >
            <MessageCircle size={16} />
            WhatsApp us
          </a>
        </div>
        <div className="lg:col-span-2">
          <p className="eyebrow !text-ice mb-4">Sports</p>
          <ul className="space-y-2.5">
            {SPORT_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid={`footer-sport-${l.label.toLowerCase()}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <p className="eyebrow !text-ice mb-4">Products</p>
          <ul className="space-y-2.5">
            {PRODUCTS.map((p) => (
              <li key={p}>
                <Link to="/#what-we-make" className="text-sm text-slate-300 hover:text-white transition-colors" data-testid={`footer-product-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {p}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2">
          <p className="eyebrow !text-ice mb-4">Company</p>
          <ul className="space-y-2.5">
            {COMPANY_LINKS.map((l) => (
              <li key={l.label}>
                <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className="eyebrow !text-ice mb-4">Contact</p>
          <ul className="space-y-3.5 text-sm">
            <li>
              <a href={CONTACT.phoneTel} data-testid="footer-phone-link" className="inline-flex items-center gap-2.5 text-slate-300 hover:text-white transition-colors">
                <MessageCircle size={15} className="text-ice shrink-0" />
                {CONTACT.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="inline-flex items-center gap-2.5 text-slate-300 hover:text-white transition-colors">
                <Mail size={15} className="text-ice shrink-0" />
                {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5 text-slate-300">
              <MapPin size={15} className="text-ice shrink-0 mt-0.5" />
              <span data-testid="footer-address">{CONTACT.address}</span>
            </li>
          </ul>
          <Link to="/contact" data-testid="footer-cta-quote" className="mt-6 inline-flex items-center gap-2 font-display font-semibold uppercase text-sm tracking-wider text-ice hover:text-white transition-colors">
            Request a Quote <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-[11px] uppercase tracking-[0.16em] text-slate-400">
          <span data-testid="footer-copyright">© {year} Top Player Sportswear</span>
          <span>Custom sportswear manufacturer — Sialkot, Pakistan</span>
        </div>
      </div>
    </footer>
  );
}
